import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send, Inbox, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export default function MessagesPage() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = () => api.get("/messages").then((r) => { setThreads(r.data); setLoading(false); });
  const loadThread = (id) => api.get(`/messages/${id}`).then((r) => setMessages(r.data));

  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 6000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!active) return;
    loadThread(active.thread_id);
    const t = setInterval(() => loadThread(active.thread_id), 4000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!reply.trim()) return;
    await api.post(`/messages/${active.thread_id}`, { text: reply.trim() });
    setReply("");
    loadThread(active.thread_id);
    loadThreads();
  };

  const suggest = async () => {
    if (!active || suggesting) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/messages/${active.thread_id}/ai-suggest`);
      setReply(data.suggestion || "");
      toast.success("AI draft ready — review & edit before sending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate a suggestion");
    } finally { setSuggesting(false); }
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Concierge Messages</h1>
        <p className="text-muted-foreground text-sm">Live "Ask Concierge" threads from your guests.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
        <div className="border border-border rounded-md bg-card overflow-y-auto">
          {loading ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-accent" /></div> : threads.length === 0 ? (
            <div className="text-center py-16 px-4"><Inbox className="w-7 h-7 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No messages yet.</p></div>
          ) : threads.map((t) => (
            <button key={t.thread_id} onClick={() => setActive(t)} data-testid={`thread-${t.thread_id}`}
              className={`w-full text-left p-4 border-b border-border transition-colors ${active?.thread_id === t.thread_id ? "bg-secondary" : "hover:bg-secondary/50"}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-foreground text-sm">{t.guest_name}{t.room ? ` · Room ${t.room}` : ""}</p>
                {t.unread > 0 && <Badge className="bg-accent text-accent-foreground border-0">{t.unread}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">{t.last_text}</p>
            </button>
          ))}
        </div>

        <div className="md:col-span-2 border border-border rounded-md bg-card flex flex-col">
          {active ? (
            <>
              <div className="p-4 border-b border-border">
                <p className="font-serif text-lg text-foreground">{active.guest_name}{active.room ? ` · Room ${active.room}` : ""}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "staff" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                      <p>{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.sender === "staff" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-border space-y-2">
                <Button onClick={suggest} disabled={suggesting} variant="outline" size="sm" data-testid="ai-suggest-btn" className="gap-1.5">
                  {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-accent" />}
                  Suggest reply with AI
                </Button>
                <div className="flex gap-2 items-end">
                  <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Type a reply… (Enter to send, Shift+Enter for a new line)" data-testid="reply-input" className="resize-none" />
                  <Button onClick={send} data-testid="reply-send-btn" className="bg-primary text-primary-foreground h-auto self-stretch"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div><MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">Select a conversation to reply.</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
